import { Head, Link } from "@inertiajs/react";
import { Download, WandSparkles, X } from "lucide-react";
import { useCallback, useMemo, useRef } from "react";

import AppLogoIcon from "@/components/app-logo-icon";
import { SiteFooter } from "@/components/site-footer";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

interface SocialImage {
  id: string;
  title?: string;
  description?: string;
  url: string;
  style?: string;
  customer_email?: string;
  images: {
    id: string;
    download_url: string;
    filename: string;
  }[];
}

interface FreeProps {
  social_image: SocialImage;
}

export default function Free({ social_image }: FreeProps) {
  const variations = useMemo(
    () => social_image.images.filter((image) => !image.filename.includes("screenshot")),
    [social_image.images],
  );

  const upsellRef = useRef<HTMLDivElement>(null);
  const handleUpsellScroll = useCallback(() => {
    upsellRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  return (
    <>
      <Head title="Your Page Previews Are Ready" />

      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-red-50 pb-24 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950">
        <div className="container mx-auto max-w-6xl px-4 py-16">
          {/* Header */}
          <header className="mb-16 text-center">
            <div className="mx-auto mb-6 flex h-12 w-12 items-center justify-center rounded-xl bg-green-500">
              <AppLogoIcon className="h-6 w-6 text-white" />
            </div>
            <h1 className="mb-4 text-4xl font-bold tracking-tight text-gray-900 dark:text-white">
              Your Page Previews Are Ready!
            </h1>
            <p className="mx-auto max-w-2xl text-lg text-gray-600 dark:text-gray-300">
              Download the options below, share them with your team, and pick the image that best fits your next launch.
            </p>
          </header>

          {/* Images Grid */}
          <div className={`mb-20 grid gap-8 lg:grid-cols-${variations.length}`}>
            {variations.map((image, index) => (
              <div
                key={image.id}
                className="group flex flex-col overflow-hidden rounded-2xl border border-white/70 bg-white/80 shadow-xl backdrop-blur transition-transform hover:-translate-y-1 hover:shadow-2xl dark:border-white/5 dark:bg-gray-900/80"
              >
                <div className="relative aspect-[1.91/1] bg-gray-100 dark:bg-gray-800">
                  <img
                    src={image.download_url}
                    alt={`${social_image.title} - Variation ${index + 1}`}
                    className="h-full w-full object-cover"
                  />
                  <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                    <button
                      type="button"
                      onClick={handleUpsellScroll}
                      className="focus -visible:opacity-100 pointer-events-auto inline-flex cursor-pointer items-center gap-2 rounded-full bg-black/60 px-4 py-2 text-xs font-semibold tracking-wide text-white uppercase opacity-0 transition-opacity duration-200 group-hover:opacity-100 hover:bg-black/70 focus-visible:ring-2 focus-visible:ring-white/80 focus-visible:outline-none"
                      aria-label="Want unlimited refinements?"
                    >
                      Want unlimited refinements?
                    </button>
                  </div>
                </div>
                <div className="flex flex-1 flex-col gap-4 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Variation {index + 1}</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Polished preview tailored to your metadata. Swap instantly if you need a different vibe.
                  </p>
                  <div className="mt-auto flex flex-col gap-2">
                    <a
                      href={image.download_url}
                      download={image.filename}
                      className="flex cursor-pointer items-center justify-center gap-2 rounded-lg bg-orange-500 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-orange-600 sm:px-4"
                    >
                      <Download className="h-4 w-4" />
                      <span>Download</span>
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* How to Use Section */}
          <div className="mb-24 overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-xl dark:border-gray-800 dark:bg-gray-900">
            <div className="px-8 py-10 sm:px-12 sm:py-12">
              <div className="mb-8 text-center">
                <h2 className="text-3xl font-bold text-gray-900 dark:text-white">How to Use Your New Social Image</h2>
              </div>

              <div className="mt-10 grid gap-6 md:grid-cols-3">
                <div className="space-y-3">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-orange-500 text-white">
                    <span className="text-xl font-bold">1</span>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Download the Image</h3>
                  <p className="text-sm leading-relaxed text-gray-600 dark:text-gray-400">
                    Choose your favorite variation, download the image and upload it to your server or CDN to use it in
                    your meta tags
                  </p>
                </div>

                <div className="space-y-3">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-orange-500 text-white">
                    <span className="text-xl font-bold">2</span>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Update Your Meta Tags (HTML)</h3>
                  <p className="text-sm leading-relaxed text-gray-600 dark:text-gray-400">
                    Update the Open Graph meta tags from your HTML head to use the new image file
                  </p>
                </div>

                <div className="space-y-3">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-orange-500 text-white">
                    <span className="text-xl font-bold">3</span>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Share and Engage (Social Media)
                  </h3>
                  <p className="text-sm leading-relaxed text-gray-600 dark:text-gray-400">
                    Your page now shows a beautiful preview on social media
                  </p>
                </div>
              </div>

              <div className="mt-10">
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-6 dark:border-amber-800 dark:bg-amber-900/20">
                  <div className="flex gap-3">
                    <div className="flex-shrink-0">
                      <svg
                        className="h-5 w-5 text-amber-600 dark:text-amber-400"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <div className="text-sm text-gray-700 dark:text-gray-300">
                      <p className="mb-2 font-semibold">
                        Important: Social networks cache preview images. After updating your meta tags:
                      </p>
                      <ul className="ml-4 list-disc space-y-1 text-gray-600 dark:text-gray-400">
                        <li>
                          Use{" "}
                          <a
                            href="https://developers.facebook.com/tools/debug/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-medium text-orange-600 hover:underline dark:text-orange-400"
                          >
                            Facebook&apos;s Sharing Debugger
                          </a>{" "}
                          or{" "}
                          <a
                            href="https://cards-dev.twitter.com/validator"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-medium text-orange-600 hover:underline dark:text-orange-400"
                          >
                            X&apos;s Card Validator
                          </a>{" "}
                          to clear the cache
                        </li>
                        <li>Even after clearing, it may take up to 24 hours for the new image to appear</li>
                        <li>New shares will show the updated image immediately</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <footer className="border-t border-white/70 pt-10 text-center dark:border-white/10">
            <div className="flex flex-col items-center justify-center gap-3 text-gray-600 dark:text-gray-300">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-orange-500">
                <AppLogoIcon className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="text-sm tracking-[0.2em] uppercase">Share-ready in minutes</p>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">Crafted by OG Pilot</p>
              </div>
            </div>
          </footer>
        </div>
      </div>

      {/* SaaS Upsell Section */}
      <div className="bg-gray-50 py-24 dark:bg-gray-950">
        <div className="container mx-auto max-w-5xl px-4">
          <div id="upsell" ref={upsellRef}>
            <Card className="relative overflow-hidden border border-gray-200 bg-white shadow-xl dark:border-gray-800 dark:bg-gray-900">
              <CardHeader className="relative z-10 text-center">
                <p className="mx-auto mb-3 inline-flex items-center gap-2 rounded-full bg-orange-100 px-4 py-1 text-xs font-semibold tracking-[0.25em] text-orange-700 uppercase dark:bg-orange-500/20 dark:text-orange-200">
                  OG Auto-Pilot
                </p>
                <CardTitle className="text-3xl leading-tight font-bold text-balance text-gray-900 dark:text-white">
                  Automate all your apps&apos;s social image generation
                </CardTitle>
                <CardDescription className="mx-auto max-w-2xl text-base text-gray-600 dark:text-gray-300">
                  Upgrade to OG Auto-Pilot and keep your previews on-brand across every page. Automate delivery and
                  iterate as often as you need.
                </CardDescription>
              </CardHeader>
              <CardContent className="relative z-10 space-y-10">
                <div className="flex flex-col gap-6 rounded-2xl border border-gray-200 bg-gray-50 p-6 shadow-sm dark:border-gray-700 dark:bg-gray-800">
                  <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Launch-ready plans</h3>
                    <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                      <span>Why teams upgrade:</span>
                      <div className="flex flex-wrap gap-2">
                        <span className="rounded-full bg-white px-2.5 py-0.5 text-xs font-medium text-gray-600 shadow-sm dark:bg-gray-700 dark:text-gray-300">
                          Domain management
                        </span>
                        <span className="rounded-full bg-white px-2.5 py-0.5 text-xs font-medium text-gray-600 shadow-sm dark:bg-gray-700 dark:text-gray-300">
                          Dynamic images
                        </span>
                        <span className="rounded-full bg-white px-2.5 py-0.5 text-xs font-medium text-gray-600 shadow-sm dark:bg-gray-700 dark:text-gray-300">
                          Global CDN
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                    {/* Starter Plan */}
                    <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800">
                      <p className="text-sm font-semibold tracking-wide text-orange-500 uppercase">Starter</p>
                      <div className="mt-2 flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-400">
                        <WandSparkles className="h-3.5 w-3.5" />
                        <span>0 credits</span>
                      </div>
                      <p className="mt-2 flex items-baseline gap-2 text-3xl font-bold text-gray-900 dark:text-white">
                        $5
                      </p>
                      <p className="text-xs tracking-wide text-gray-500 uppercase dark:text-gray-400">per month</p>
                      <ul className="mt-4 space-y-2 text-sm text-gray-600 dark:text-gray-300">
                        <li className="flex items-center gap-2">
                          <span className="text-green-500">✓</span>
                          <span className="font-medium">Unlimited domains</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <span className="text-green-500">✓</span>
                          <span className="font-medium">Unlimited image hits</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="mt-0.5 text-green-500">✓</span>
                          <span className="font-medium">Up to 10,000 pages</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <span className="text-green-500">✓</span>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="border-base-content/30 hover:border-primary cursor-help border-b-2 border-dashed duration-150 hover:border-solid dark:border-orange-500">
                                $9 per extra <span className="font-bold">30</span> credits
                              </span>
                            </TooltipTrigger>
                            <TooltipContent
                              className="max-w-xs dark:bg-gray-900 dark:text-neutral-50"
                              arrowClassName="dark:bg-gray-900 dark:fill-gray-900"
                            >
                              <p>
                                1 credit = 1 image generated with our state-of-the-art image model. <br />
                                <br /> Credits refresh monthly and are automatically credited to your account. Extra
                                credits cost $9 per 30 credits ($0.30 per credit).
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        </li>
                        <li className="flex items-center gap-2">
                          <span className="text-green-500">✓</span>
                          <span>Global CDN delivery</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <span className="text-green-500">✓</span>
                          <span>Email support</span>
                        </li>
                        <li className="flex items-center gap-2 text-gray-400">
                          <X size={12} className="text-gray-400" />
                          <span>Social media analytics</span>
                        </li>
                      </ul>
                    </div>

                    {/* Builder Plan */}
                    <div className="rounded-2xl border border-orange-500 bg-gradient-to-br from-orange-500 to-orange-600 p-5 text-white shadow-lg">
                      <div className="mb-2 inline-block rounded-full bg-white/20 px-3 py-1 text-xs font-semibold">
                        Recommended
                      </div>
                      <p className="text-sm font-semibold tracking-wide uppercase">Builder</p>
                      <div className="mt-2 flex items-center gap-1.5 text-xs opacity-90">
                        <WandSparkles className="h-3.5 w-3.5" />
                        <span>100 credits</span>
                      </div>
                      <p className="mt-2 flex items-baseline gap-2 text-3xl font-bold">$49</p>
                      <p className="text-xs tracking-wide uppercase opacity-90">per month</p>
                      <ul className="mt-4 space-y-2 text-sm">
                        <li className="flex items-center gap-2">
                          <span>✓</span>
                          <span className="font-medium">Unlimited domains</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <span>✓</span>
                          <span className="font-medium">Unlimited image hits</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span>✓</span>
                          <span className="font-medium">Up to 100,000 pages</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <span>✓</span>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="cursor-help border-b-2 border-dashed border-white/30 text-white duration-150 hover:border-solid hover:border-white/50">
                                $9 per extra <span className="font-bold">30</span> credits
                              </span>
                            </TooltipTrigger>
                            <TooltipContent
                              className="max-w-xs dark:bg-gray-900 dark:text-neutral-50"
                              arrowClassName="dark:bg-gray-900 dark:fill-gray-900"
                            >
                              <p>
                                1 credit = 1 image generated with our state-of-the-art image model. <br />
                                <br /> Credits refresh monthly and are automatically credited to your account. Extra
                                credits cost $9 per 30 credits ($0.30 per credit).
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        </li>
                        <li className="flex items-center gap-2">
                          <span>✓</span>
                          <span>Global CDN delivery</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <span>✓</span>
                          <span>Email support</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <X size={12} className="text-white/50" />
                          <span className="text-white/50">Social media analytics</span>
                        </li>
                      </ul>
                    </div>

                    {/* Growth Plan */}
                    <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800">
                      <p className="text-sm font-semibold tracking-wide text-orange-500 uppercase">Growth</p>
                      <div className="mt-2 flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-400">
                        <WandSparkles className="h-3.5 w-3.5" />
                        <span>200 credits</span>
                      </div>
                      <p className="mt-2 flex items-baseline gap-2 text-3xl font-bold text-gray-900 dark:text-white">
                        $99
                      </p>
                      <p className="text-xs tracking-wide text-gray-500 uppercase dark:text-gray-400">per month</p>
                      <ul className="mt-4 space-y-2 text-sm text-gray-600 dark:text-gray-300">
                        <li className="flex items-center gap-2">
                          <span className="text-green-500">✓</span>
                          <span className="font-medium">Unlimited domains</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <span className="text-green-500">✓</span>
                          <span className="font-medium">Unlimited image hits</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="mt-0.5 text-green-500">✓</span>
                          <span className="font-medium">Up to 1,000,000 pages</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <span className="text-green-500">✓</span>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="border-base-content/30 hover:border-primary cursor-help border-b-2 border-dashed duration-150 hover:border-solid dark:border-orange-500">
                                $9 per extra <span className="font-bold">30</span> credits
                              </span>
                            </TooltipTrigger>
                            <TooltipContent
                              className="max-w-xs dark:bg-gray-900 dark:text-neutral-50"
                              arrowClassName="dark:bg-gray-900 dark:fill-gray-900"
                            >
                              <p>
                                1 credit = 1 image generated with our state-of-the-art image model. <br />
                                <br /> Credits refresh monthly and are automatically credited to your account. Extra
                                credits cost $9 per 30 credits ($0.30 per credit).
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        </li>
                        <li className="flex items-center gap-2">
                          <span className="text-green-500">✓</span>
                          <span>Global CDN delivery</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <span className="text-green-500">✓</span>
                          <span className="font-medium">Priority support</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <span className="text-green-500">✓</span>
                          <span>Social media analytics</span>
                        </li>
                      </ul>
                    </div>
                  </div>

                  <div className="mt-6 rounded-2xl border border-gray-200 bg-gradient-to-br from-gray-50 to-white p-6 shadow-sm dark:border-gray-700 dark:from-gray-800 dark:to-gray-800/50">
                    <div className="flex flex-col items-center gap-4 text-center lg:flex-row lg:justify-between lg:text-left">
                      <div className="flex-1">
                        <p className="text-base font-semibold text-gray-900 dark:text-white">Not ready to commit?</p>
                        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                          Start with a 7-day free trial • No credit card required • Cancel anytime
                        </p>
                      </div>
                      <Link
                        href="/sign_up"
                        className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 px-6 py-3.5 text-base font-semibold whitespace-nowrap text-white shadow-lg transition hover:from-orange-600 hover:to-orange-700 hover:shadow-xl lg:w-auto"
                      >
                        Get started with OG Auto-Pilot
                        <span aria-hidden="true" className="text-lg">
                          →
                        </span>
                      </Link>
                    </div>
                  </div>
                </div>

                <div className="rounded-2xl border border-gray-200 bg-gray-50 p-8 dark:border-gray-700 dark:bg-gray-800">
                  <h4 className="mb-6 text-lg font-semibold text-gray-900 dark:text-white">
                    What you get with OG Auto-Pilot
                  </h4>
                  <div className="grid gap-6 sm:grid-cols-2">
                    <div className="space-y-1">
                      <p className="flex items-center gap-2 font-semibold text-gray-900 dark:text-white">
                        <span className="text-orange-500">✓</span>
                        Unlimited refinements
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Iterate until your creatives align with your latest messaging.
                      </p>
                    </div>
                    <div className="space-y-1">
                      <p className="flex items-center gap-2 font-semibold text-gray-900 dark:text-white">
                        <span className="text-orange-500">✓</span>
                        Multiple branded styles
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Choose from over 15 styles for your pages, launches, campaigns, and partner pages.
                      </p>
                    </div>
                    <div className="space-y-1">
                      <p className="flex items-center gap-2 font-semibold text-gray-900 dark:text-white">
                        <span className="text-orange-500">✓</span>
                        Automated delivery
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Serve production assets instantly to your marketing stack.
                      </p>
                    </div>
                    <div className="space-y-1">
                      <p className="flex items-center gap-2 font-semibold text-gray-900 dark:text-white">
                        <span className="text-orange-500">✓</span>
                        Advanced analytics
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Understand how visuals impact CTR and refine with data.
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <SiteFooter className="bg-white dark:bg-gray-950" showSeparator={false} />
    </>
  );
}
